
import { useState, useEffect } from "react";
import { Building2, Calendar, BedDouble, CheckCircle, Eye, EyeOff, MapPin } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import DateRangePicker from "../../components/DateRangePicker";
import { reportsApi } from "@/lib/api";
import { localToday } from "@/lib/data";

type Tab = "timeline" | "venues" | "rooms";

const STATUS_COLOR: Record<string, string> = {
  confirmed:   "bg-blue-100 text-blue-700 border-blue-200",
  completed:   "bg-emerald-100 text-emerald-700 border-emerald-200",
  cancelled:   "bg-red-100 text-red-700 border-red-200",
  in_progress: "bg-amber-100 text-amber-700 border-amber-200",
  pending:     "bg-stone-100 text-stone-600 border-stone-200",
  booked:      "bg-indigo-100 text-indigo-700 border-indigo-200",
  checked_in:  "bg-teal-100 text-teal-700 border-teal-200",
  checked_out: "bg-slate-100 text-slate-600 border-slate-200",
};

const EVENT_COLORS = [
  "bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-amber-500",
  "bg-rose-500", "bg-cyan-500", "bg-indigo-500", "bg-orange-500",
];

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_COLOR[status] ?? "bg-stone-100 text-stone-600 border-stone-200";
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border capitalize ${cls}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color, bg }: any) {
  return (
    <div className={`${bg} rounded-xl p-4 border border-stone-100`}>
      <div className={`w-9 h-9 rounded-lg ${color} bg-white/80 flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs font-medium text-stone-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-stone-800">{value}</p>
      {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
    </div>
  );
}

// Build a stable color map for event IDs
function useEventColors(events: any[], roomBookings: any[]) {
  const ids = new Set<number>();
  events.forEach((e: any) => ids.add(e.event_id));
  roomBookings.forEach((b: any) => ids.add(b.event_id));
  const map: Record<number, string> = {};
  let i = 0;
  ids.forEach(id => { map[id] = EVENT_COLORS[i++ % EVENT_COLORS.length]; });
  return map;
}

export default function VenueUtilizationPage() {
  const today = localToday();
  const firstOfMonth = today.slice(0, 7) + "-01";

  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate]     = useState(today);
  const [data, setData]         = useState<any>(null);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState<Tab>("timeline");
  const [showFree, setShowFree] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await reportsApi.venueUtilization({ from_date: fromDate, to_date: toDate });
      setData(res.data?.data);
    } catch { /* empty */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [fromDate, toDate]);

  const timeline: any[]  = data?.timeline ?? [];
  const venues: any[]    = data?.venues ?? [];
  const rooms: any[]     = data?.rooms ?? [];
  const summary          = data?.summary ?? {};

  // Collect all event IDs for color mapping
  const allEventIds = [
    ...timeline.flatMap((d: any) => [...d.venues, ...d.rooms]),
    ...venues.flatMap((v: any) => v.bookings),
    ...rooms.flatMap((r: any) => r.bookings),
  ];
  const colorMap = useEventColors(allEventIds, []);

  const visibleDays = showFree ? timeline : timeline.filter((d: any) => d.occupied);

  const tabs: { key: Tab; label: string; icon: any }[] = [
    { key: "timeline", label: "Daily Timeline", icon: Calendar },
    { key: "venues",   label: "By Venue",       icon: Building2 },
    { key: "rooms",    label: "By Room",         icon: BedDouble },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-teal-600" />
            <h1 className="text-lg font-bold text-stone-800">Venue Utilization</h1>
          </div>
          <DateRangePicker
            fromDate={fromDate} toDate={toDate}
            onFromChange={setFromDate} onToChange={setToDate}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 text-stone-400 text-sm">Loading…</div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard
                label="Occupied Days"
                value={summary.occupied_days ?? 0}
                sub={`of ${summary.total_days ?? 0} total days`}
                icon={Calendar} color="text-teal-600" bg="bg-teal-50"
              />
              <KpiCard
                label="Occupancy Rate"
                value={`${summary.occupancy_rate ?? 0}%`}
                sub={`${summary.free_days ?? 0} free days`}
                icon={CheckCircle} color="text-emerald-600" bg="bg-emerald-50"
              />
              <KpiCard
                label="Venue Bookings"
                value={summary.venue_booking_count ?? 0}
                sub={`${summary.venues_with_bookings ?? 0} venues active`}
                icon={Building2} color="text-blue-600" bg="bg-blue-50"
              />
              <KpiCard
                label="Room Bookings"
                value={summary.room_booking_count ?? 0}
                sub={`${summary.rooms_with_bookings ?? 0} rooms occupied`}
                icon={BedDouble} color="text-violet-600" bg="bg-violet-50"
              />
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl border border-stone-200">
              <div className="flex items-center border-b border-stone-100 px-4">
                {tabs.map(t => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setTab(t.key)}
                      className={`flex items-center gap-1.5 px-3 py-3 text-xs font-medium border-b-2 transition-colors -mb-px ${
                        tab === t.key
                          ? "border-teal-500 text-teal-700"
                          : "border-transparent text-stone-500 hover:text-stone-700"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <div className="p-4">
                {/* ── TIMELINE TAB ── */}
                {tab === "timeline" && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-stone-500">
                        {summary.occupied_days ?? 0} occupied days in range
                      </p>
                      <button
                        onClick={() => setShowFree(v => !v)}
                        className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-teal-600 transition-colors"
                      >
                        {showFree ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        {showFree ? "Hide free days" : "Show all days"}
                      </button>
                    </div>

                    {visibleDays.length === 0 ? (
                      <p className="text-sm text-stone-400 py-8 text-center">
                        No bookings in this period
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {visibleDays.map((day: any) => (
                          <div
                            key={day.date}
                            className={`rounded-lg border ${
                              day.is_today
                                ? "border-teal-300 bg-teal-50/50"
                                : day.occupied
                                ? "border-stone-200 bg-white"
                                : "border-stone-100 bg-stone-50/50"
                            }`}
                          >
                            {/* Date header */}
                            <div className={`flex items-center justify-between px-3 py-2 rounded-t-lg ${
                              day.occupied ? "bg-stone-50" : ""
                            }`}>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center ${
                                  day.is_today
                                    ? "bg-teal-500 text-white"
                                    : day.is_sunday
                                    ? "bg-red-100 text-red-600"
                                    : "bg-stone-200 text-stone-600"
                                }`}>
                                  {new Date(day.date).getDate()}
                                </span>
                                <span className="text-xs font-semibold text-stone-700">{day.day_label}</span>
                                {day.is_today && (
                                  <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-medium">Today</span>
                                )}
                              </div>
                              {!day.occupied && (
                                <span className="text-[10px] text-stone-400">Free</span>
                              )}
                            </div>

                            {/* Venue bookings */}
                            {day.venues.length > 0 && (
                              <div className="px-3 py-2 space-y-1.5 border-t border-stone-100">
                                <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wide flex items-center gap-1">
                                  <Building2 className="w-3 h-3" /> Venues
                                </p>
                                {day.venues.map((v: any, i: number) => (
                                  <div key={i} className="flex items-center gap-2 flex-wrap">
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${colorMap[v.event_id] ?? "bg-stone-400"}`} />
                                    <span className="text-xs font-medium text-stone-800">{v.venue_name}</span>
                                    <span className="text-xs text-stone-500">→</span>
                                    <span className="text-xs text-stone-700">{v.event_name}</span>
                                    {v.client && <span className="text-xs text-stone-400">({v.client})</span>}
                                    <StatusBadge status={v.status} />
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Room bookings */}
                            {day.rooms.length > 0 && (
                              <div className={`px-3 py-2 space-y-1.5 ${day.venues.length > 0 ? "border-t border-stone-100" : "border-t border-stone-100"}`}>
                                <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wide flex items-center gap-1">
                                  <BedDouble className="w-3 h-3" /> Rooms
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {day.rooms.map((r: any, i: number) => (
                                    <div key={i} className="flex items-center gap-1.5 bg-stone-50 border border-stone-200 rounded-lg px-2 py-1">
                                      <div className={`w-1.5 h-1.5 rounded-full ${colorMap[r.event_id] ?? "bg-stone-400"}`} />
                                      <span className="text-xs font-medium text-stone-700">{r.room_name}</span>
                                      {r.room_type && <span className="text-[10px] text-stone-400">{r.room_type}</span>}
                                      {r.event_name && (
                                        <>
                                          <span className="text-[10px] text-stone-400">·</span>
                                          <span className="text-xs text-stone-600">{r.event_name}</span>
                                        </>
                                      )}
                                      {r.customer_name && (
                                        <>
                                          <span className="text-[10px] text-stone-400">·</span>
                                          <span className="text-xs text-stone-500">{r.customer_name}</span>
                                        </>
                                      )}
                                      <span className="text-[10px] text-stone-400 ml-1">
                                        {r.check_in} – {r.check_out}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── VENUES TAB ── */}
                {tab === "venues" && (
                  <div className="space-y-4">
                    {venues.filter((v: any) => v.bookings.length > 0).length === 0 ? (
                      <p className="text-sm text-stone-400 py-8 text-center">No venue bookings in this period</p>
                    ) : (
                      venues.map((venue: any) => (
                        <div key={venue.id} className="rounded-lg border border-stone-200 overflow-hidden">
                          {/* Venue header */}
                          <div className="flex items-center justify-between bg-stone-50 px-4 py-3 border-b border-stone-200">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-teal-600" />
                              <span className="text-sm font-semibold text-stone-800">{venue.name}</span>
                              {venue.location && (
                                <span className="flex items-center gap-0.5 text-xs text-stone-400">
                                  <MapPin className="w-3 h-3" />{venue.location}
                                </span>
                              )}
                              {venue.capacity && (
                                <span className="text-[10px] bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded-full">
                                  Cap: {venue.capacity}
                                </span>
                              )}
                            </div>
                            <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-100">
                              {venue.bookings.length} booking{venue.bookings.length !== 1 ? "s" : ""}
                            </span>
                          </div>

                          {/* Bookings table */}
                          {venue.bookings.length === 0 ? (
                            <div className="px-4 py-3 text-xs text-stone-400">No bookings in this period</div>
                          ) : (
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-stone-100">
                                  <th className="text-left px-4 py-2 text-xs font-semibold text-stone-500">Date</th>
                                  <th className="text-left px-4 py-2 text-xs font-semibold text-stone-500">Event</th>
                                  <th className="text-left px-4 py-2 text-xs font-semibold text-stone-500">Client</th>
                                  <th className="text-left px-4 py-2 text-xs font-semibold text-stone-500">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-stone-50">
                                {venue.bookings.map((b: any, i: number) => (
                                  <tr key={i} className="hover:bg-stone-50">
                                    <td className="px-4 py-2.5">
                                      <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${colorMap[b.event_id] ?? "bg-stone-400"}`} />
                                        <span className="text-xs font-medium text-stone-700">{b.date}</span>
                                        <span className="text-[10px] text-stone-400">
                                          {new Date(b.date).toLocaleDateString("en-IN", { weekday: "short" })}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2.5 font-medium text-stone-800 text-xs">{b.event_name}</td>
                                    <td className="px-4 py-2.5 text-stone-500 text-xs">{b.client ?? "—"}</td>
                                    <td className="px-4 py-2.5"><StatusBadge status={b.status} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* ── ROOMS TAB ── */}
                {tab === "rooms" && (
                  <div className="space-y-4">
                    {rooms.filter((r: any) => r.bookings.length > 0).length === 0 ? (
                      <p className="text-sm text-stone-400 py-8 text-center">No room bookings in this period</p>
                    ) : (
                      rooms
                        .filter((r: any) => r.bookings.length > 0)
                        .map((room: any) => (
                          <div key={room.id} className="rounded-lg border border-stone-200 overflow-hidden">
                            {/* Room header */}
                            <div className="flex items-center justify-between bg-stone-50 px-4 py-3 border-b border-stone-200">
                              <div className="flex items-center gap-2">
                                <BedDouble className="w-4 h-4 text-violet-600" />
                                <span className="text-sm font-semibold text-stone-800">{room.name}</span>
                                {room.type && (
                                  <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">
                                    {room.type}
                                  </span>
                                )}
                                {room.capacity && (
                                  <span className="text-[10px] text-stone-400">Capacity: {room.capacity}</span>
                                )}
                                {room.rate > 0 && (
                                  <span className="text-[10px] text-stone-400">₹{room.rate}/night</span>
                                )}
                              </div>
                              <span className="text-xs font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full border border-violet-100">
                                {room.bookings.length} booking{room.bookings.length !== 1 ? "s" : ""}
                              </span>
                            </div>

                            {/* Bookings */}
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-stone-100">
                                  <th className="text-left px-4 py-2 text-xs font-semibold text-stone-500">Check-in</th>
                                  <th className="text-left px-4 py-2 text-xs font-semibold text-stone-500">Check-out</th>
                                  <th className="text-right px-4 py-2 text-xs font-semibold text-stone-500">Nights</th>
                                  <th className="text-left px-4 py-2 text-xs font-semibold text-stone-500">Event</th>
                                  <th className="text-left px-4 py-2 text-xs font-semibold text-stone-500">Guest</th>
                                  <th className="text-left px-4 py-2 text-xs font-semibold text-stone-500">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-stone-50">
                                {room.bookings.map((b: any, i: number) => (
                                  <tr key={i} className="hover:bg-stone-50">
                                    <td className="px-4 py-2.5">
                                      <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${colorMap[b.event_id] ?? "bg-stone-400"}`} />
                                        <span className="text-xs font-medium text-stone-700">{b.check_in}</span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-stone-700">{b.check_out}</td>
                                    <td className="px-4 py-2.5 text-right">
                                      <span className="text-xs font-semibold text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">
                                        {b.nights}N
                                      </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-xs font-medium text-stone-800">{b.event_name ?? "—"}</td>
                                    <td className="px-4 py-2.5 text-xs text-stone-500">
                                      {b.customer_name || b.client || "—"}
                                    </td>
                                    <td className="px-4 py-2.5"><StatusBadge status={b.status} /></td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="bg-stone-50 border-t border-stone-200">
                                  <td colSpan={2} className="px-4 py-2 text-xs text-stone-500">Total nights</td>
                                  <td className="px-4 py-2 text-right">
                                    <span className="text-xs font-bold text-violet-700">
                                      {room.bookings.reduce((s: number, b: any) => s + b.nights, 0)}N
                                    </span>
                                  </td>
                                  <td colSpan={3} className="px-4 py-2 text-xs text-stone-400">
                                    {room.rate > 0
                                      ? `Est. revenue: ₹${(room.bookings.reduce((s: number, b: any) => s + b.nights * (b.rate || room.rate), 0)).toLocaleString("en-IN")}`
                                      : ""}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
